import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    HostBinding,
    OnDestroy,
    OnInit,
    ViewChild
} from "@angular/core";
import {FilterModel, provideFilterModel} from "../../../framework/bi/filter/filter.model";
import {first, Subscription} from "rxjs";
import {
    ConfigurationService,
    JwtHttpClientService,
    provideEntityModel,
    provideSearchModel
} from "@fedeltapointofsale/data-client";
import {ActivatedRoute, Params, Router} from "@angular/router";
import {DashboardEntityModel} from "../dashboard-entity.model";
import {BIService} from "../../../framework/bi/bi.service";
import {DashboardLayout, DashboardRow} from "../dashboard-layout";
import {ChartMemberSelection} from "../../../framework/bi/chart/chart-member-selection";
import {FUIAlertActions, FUIAlertService, FUISpinnersService} from "@fedeltapointofsale/ui";
import {BIPageSearchModel} from "../../bi-page/bi-page-search.model";
import {BiPageSelectNavComponent} from "../../bi-page/controls/bi-page-select-nav.component";
import {DashboardModel} from "../dashboard.model";
import {CubeDefinitionData} from "../../../framework/bi/cube-definition.data";
import {BIPageData} from "../../bi-page/bi-page.data";
import {Config, ServerAuth} from "../../../config";
import {DashboardData} from "../dashboard.data";
import {ServerStatus, ServerStatusData} from "../../../framework/bi/server-status/server-status.data";
import {ServerStatusModel} from "../../../framework/bi/server-status/server-status.model";

@Component({
    selector: "wbo-bi-dashboard-view-page",
    templateUrl: "./dashboard-view-page.component.html",
    styleUrls: ["./dashboard-view-page.component.scss"],
    providers: [
        provideSearchModel(BIPageSearchModel),
        provideFilterModel(),
        provideEntityModel(DashboardEntityModel),
        BIService,
        DashboardModel,
        ServerStatusModel
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DashboardViewPageComponent implements AfterViewInit, OnDestroy, OnInit {
    @ViewChild(BiPageSelectNavComponent) selectNav: BiPageSelectNavComponent;
    id: string = "";
    layout: DashboardLayout;
    cubeDefinition: CubeDefinitionData;
    childHierarchies: { [key: string]: string; };
    editable: boolean;
    clonable: boolean;
    deletable: boolean;
    @HostBinding("style.display") private display: string = "flex";
    @HostBinding("style.width") private width: string = "100%";
    private subscriptions: Subscription = new Subscription();

    constructor(private http: JwtHttpClientService,
                public filterModel: FilterModel,
                public entityModel: DashboardEntityModel,
                public biPageSearchModel: BIPageSearchModel,
                private route: ActivatedRoute,
                private router: Router,
                private alertService: FUIAlertService,
                private changeDetectorRef: ChangeDetectorRef,
                private configService: ConfigurationService,
                private biService: BIService,
                public dashboardModel: DashboardModel,
                public serverStatusModel: ServerStatusModel,
                private spinnerService: FUISpinnersService) {
    }

    ngOnInit() {
        this.configService.getBoolean(Config.BI_ENABLED).then(biEnabled => {
            if (!biEnabled) {
                this.router.navigate(["/main/home"], {replaceUrl: true});
            }
        });
    }

    ngAfterViewInit(): void {
        this.route.params.subscribe((params: Params) => {
            this.id = params["id"];
            this.loadDashboard();
        });
    }

    loadDashboard(): void {
        this.spinnerService.open(true);
        this.selectNav.refreshData();
        this.entityModel.loadEntity(this.id).then((data: DashboardData) => {
            this.entityModel.canCreateEntity().then((canCreate: boolean) => this.clonable = canCreate && this.entityModel.isEntityCloneable());

            this.configService.getBoolean(ServerAuth.PUBLISH_BI_DASHBOARD).then((canPublish: boolean) => {
                if (data.public && !canPublish) {
                    this.editable = false;
                    this.deletable = false;
                } else {
                    this.entityModel.canUpdateEntity().then((canUpdate: boolean) => this.editable = canUpdate && parseInt(this.id) >= 0);
                    this.entityModel.canDeleteEntity().then((canDelete: boolean) => this.deletable = canDelete && parseInt(this.id) >= 0);
                }
            });

            this.layout = undefined;
            this.changeDetectorRef.markForCheck();
            this.subscriptions.add(this.serverStatusModel.statusUpdate$.subscribe((status: ServerStatusData) => {
                this.spinnerService.close();
                if (status.status === ServerStatus.INITIALISED) {
                    this.loadCube();
                } else {
                    console.log("BI Server status: " + status.status);
                    console.log(status.percentComplete + "% complete");
                }
                this.changeDetectorRef.markForCheck();
            }));
        }).catch(error => {
            this.spinnerService.close();
            throw error;
        });
    }

    loadCube(): void {
        this.spinnerService.open(true);
        this.biService.getCube(this.entityModel.data.cubeName).then((cube) => {
            this.cubeDefinition = cube;
            this.filterModel.init(cube,
                this.entityModel.data.dateRangeRef,
                this.entityModel.data.filterMembers);

            this.layout = DashboardLayout.buildLayout(this.entityModel.data, true);
            this.changeDetectorRef.markForCheck();

            this.spinnerService.close();
        }).catch(error => {
            this.spinnerService.close();
            throw error;
        });
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    public edit(): void {
        if (this.id) {
            this.router.navigate(["/main/dashboard-edit/" + this.id]);
        } else {
            this.router.navigate(["/main/dashboard-edit/new"]);
        }
    }

    public delete(): void {
        this.alertService.showConfirmationOkCancel("wbo.bi-dashboard.prompt.remove.heading", "wbo.bi-dashboard.prompt.remove.message").subscribe((action) => {
            if (action === FUIAlertActions.OK_ACTION) {
                this.entityModel.deleteEntity(this.id);

                this.biPageSearchModel.filter["pageType"] = BIPageData.PAGE_TYPE_DASHBOARD;

                this.biPageSearchModel.search().pipe(first()).subscribe(() => {
                    if (this.biPageSearchModel.data.length === 0) {
                        this.router.navigate(["/main"]);
                    } else {
                        this.router.navigate(["/main/dashboard/" + this.biPageSearchModel.data[0].id]);
                    }
                });
            }
        });
    }

    public clone(): void {
        this.router.navigate(["main/dashboard-edit/new"], {queryParams: {clone: this.id}});
    }

    public reset(): void {
        this.loadDashboard();
    }

    refresh(): void {
        this.filterModel.notifyFilterChange();
    }

    memberSelected(event: ChartMemberSelection): void {
        this.filterModel.filterSelectedMembers(event.dataMembers, event.dataLabels);
    }

    public getChartHeight(row: DashboardRow): number {
        let height = 400;
        row.columns.forEach((dashBoardChartData) => {
            if (dashBoardChartData.chartType === "summary") {
                height = 200;
            }
        });

        return height;
    }
}
